import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { statusColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';

import { Button } from '../Button/Button';
import { Icon, type IconName } from '../Icon/Icon';
import { alertBodyStyle, alertStyle, alertTitleStyle, closeButtonStyle } from './Alert.css';

type Props = ComponentPropsWithRef<'div'> & {
  /** Set to `true` when the alert is rendered conditionally so screen readers announce it automatically. */
  announce: boolean;
  children: ReactNode;
  dismissible?: boolean;
  icon: IconName;
  onDismiss?: () => void;
  status: keyof typeof statusColorThemes;
  title?: string;
};

export const Alert = ({ announce, children, dismissible = false, icon, onDismiss, status, title, ...delegated }: Props) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const { t } = useTranslation(['common']);

  if (isDismissed) return null;

  return (
    <div
      {...delegated}
      className={clsx(alertStyle, statusColorThemes[status], delegated.className)}
      role={announce ? 'alert' : delegated.role}
    >
      <Icon name={icon} />

      <div className={alertBodyStyle}>
        {title && <p className={alertTitleStyle}>{title}</p>}

        {children}
      </div>

      {dismissible && (
        <Button
          aria-label={t('common:actions.close')}
          className={closeButtonStyle}
          icon="CloseSmall"
          onClick={() => {
            setIsDismissed(true);
            onDismiss?.();
          }}
        />
      )}
    </div>
  );
};
